import { useMemo, useState } from "react";
import { Search, SearchX } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow, MaskedLines } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import { PRODUCTS, CATEGORIES } from "../data/products";

const Products = () => {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [quick, setQuick] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const inCategory = category === "all" || p.category === category;
      const inQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q);
      return inCategory && inQuery;
    });
  }, [category, query]);

  return (
    <div data-testid="page-products">
      <Seo
        title="Products"
        description="Filter the Aurik range by category: palatants and sweeteners, toxin management, gut health and acidifiers, emulsifiers, herbal extracts and mineral nutrition."
      />

      <section className="grain mesh-glow relative overflow-hidden pb-16 pt-40 lg:pt-48">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow dark>The portfolio</Eyebrow>
          <MaskedLines
            as="h1"
            delay={0.2}
            className="mt-7 max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            lines={["Every additive,", "one discipline: intake."]}
          />
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-emerald-100/70 sm:text-base">
            Free-flow powders for mineral mixtures, complementary feeds and complete
            feeds. Filter by category, open a quick view, or download any technical sheet.
          </p>

          <div className="mt-10 flex w-full max-w-md items-center gap-3 rounded-full border border-white/20 bg-white/5 px-5 py-3.5 backdrop-blur">
            <Search className="h-4 w-4 shrink-0 text-emerald-300" />
            <input
              data-testid="products-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products…"
              aria-label="Search products"
              className="w-full bg-transparent font-mono text-xs text-white placeholder:text-white/40 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 pt-14 sm:px-8">
        <div className="flex flex-wrap gap-2.5" data-testid="product-filters">
          {[{ slug: "all", label: "All products" }, ...CATEGORIES].map((c) => (
            <button
              key={c.slug}
              data-testid={`product-filter-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                category === c.slug
                  ? "border-emerald-800 bg-emerald-900 text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div data-testid="products-empty-state" className="mt-16 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-slate-300 py-20 text-center">
            <SearchX className="h-8 w-8 text-slate-400" />
            <p className="font-display text-xl font-bold text-night">No products match your filters</p>
            <p className="max-w-sm text-sm text-slate-500">
              Try a different search term or category — or ask us directly at info@fnni.in.
            </p>
            <button
              data-testid="products-clear-filters"
              onClick={() => { setCategory("all"); setQuery(""); }}
              className="rounded-full bg-night px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-white"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} onQuickView={setQuick} />
            ))}
          </div>
        )}
      </section>

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
};

export default Products;
